import type { TFunction } from 'i18next';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useInRouterContext, useLocation } from 'react-router';

import { ApiError } from '@/shared/api/client';
import { cn } from '@/shared/utils/cn';
import { Alert } from './Alert';
import { Button, ButtonLink } from './Button';
import { PageHeader } from './PageHeader';

/**
 * Skeleton shown while a list or panel is loading.
 *
 * `delayMs` (UI-1): an inline panel that usually answers at once waits that
 * long before drawing its skeleton (300 for inline panels), so a fast answer
 * does not flash one. Without it the skeleton is immediate, as before.
 */
export function LoadingState({
  rows = 4,
  label,
  delayMs = 0,
}: {
  rows?: number;
  label?: string;
  delayMs?: number;
}) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(delayMs <= 0);

  useEffect(() => {
    if (delayMs <= 0) return;

    const timer = setTimeout(() => setShown(true), delayMs);

    return () => clearTimeout(timer);
  }, [delayMs]);

  if (!shown) return null;

  return (
    <div role="status" aria-live="polite" className="space-y-3 p-4">
      <span className="sr-only">{label ?? t('app.loading')}</span>
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="h-11 animate-pulse rounded-lg bg-active motion-reduce:animate-none"
        />
      ))}
    </div>
  );
}

/**
 * What an empty list or section is (UI-1, TASK §12): `first-run`, nothing
 * exists yet; `filtered`, records exist but the filters or search match none
 * of them (its way out is Clear all); `unavailable`, this seat may not see
 * this area. The caller decides — only it knows which is true.
 */
export type EmptyKind = 'first-run' | 'filtered' | 'unavailable';

/**
 * R-050 EmptyState — typographic by decision (Backlog B-05: no in-product
 * illustration). Card title, a meta sentence of at most ~46 characters a line,
 * and the recovery action, which differs between "nothing yet" and "nothing
 * matches these filters" and is permission-dependent — the caller decides.
 *
 * `kind` names which empty this is (`data-empty-kind`); a `filtered` state
 * with no sentence of its own says how to see more. `framed` draws its own
 * card edge; inside a Card or a bare table it is off.
 */
export function EmptyState({
  title,
  description,
  action,
  framed = true,
  tourId,
  kind,
  tourState,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  framed?: boolean;
  /** Guided-tour anchor, rendered as `data-tour` on the root (an `AnchorId`; CONTRACT §3). */
  tourId?: string;
  kind?: EmptyKind;
  /**
   * A coach's state probe, rendered as `data-guidance-state` on the root —
   * only with a `tourId`, and only a value `ANCHOR_STATES` lists for it, from
   * SERVER data (UI-6). Never a record id, a name or an amount.
   */
  tourState?: string;
}) {
  const { t } = useTranslation();
  const sentence =
    description ?? (kind === 'filtered' ? t('shared.filter.emptyFilteredHint') : undefined);

  return (
    <div
      data-tour={tourId}
      data-empty-kind={kind}
      data-guidance-state={tourId === undefined ? undefined : tourState}
      className={cn(
        'flex flex-col items-center gap-3 px-6 py-12 text-center',
        framed && 'rounded-(--radius-card) border border-line-subtle bg-card',
      )}
    >
      <p className="type-card-title text-ink-heading">{title}</p>
      {sentence ? <p className="type-meta max-w-[46ch] text-ink-muted">{sentence}</p> : null}
      {action}
    </div>
  );
}

/**
 * What a refused read says (P2-07 / DS-08 / A11Y-05): `no-access`, the server
 * will not show this to this seat (403); `not-found`, there is nothing at this
 * address for them (404 — gone, mistyped, or another organisation's).
 *
 * `filter` (W12A-01): a 403 or 404 that names the FIELD it refused
 * (`details.field`, e.g. a list's `branch_id` or `requester_id` from a shared
 * or old link). The list is the person's own; only that filter cannot be
 * applied, so the list keeps its toolbar and the way out is Clear all.
 */
export type Refusal = 'no-access' | 'not-found' | 'filter';

/** A 403 that asks for the MFA step is the session's to handle (the shell gives way), not this page's. */
const SESSION_STEPS: ReadonlySet<string> = new Set([
  'MFA_CHALLENGE_REQUIRED',
  'MFA_ENROLMENT_REQUIRED',
]);

/**
 * Refusals whose own wording is only "you may not" — about an action, not a
 * page. The state says it in its own words; every other refusal keeps the
 * server's reason (outside your purchasing nodes, not in the agreement…).
 */
const PLAIN_REFUSALS: ReadonlySet<string> = new Set([
  'FORBIDDEN',
  'PERMISSION_MISSING',
  'NOT_FOUND',
]);

/**
 * Whether a failure is really an answer: no access, or nothing there. Neither
 * changes on a retry, and neither has a request id worth reading out to
 * support — so neither is shown as a failed load.
 */
export function refusalOf(error: unknown): Refusal | null {
  if (!(error instanceof ApiError)) return null;

  const refused = error.isNotFound || (error.isForbidden && !SESSION_STEPS.has(error.code));

  if (!refused) return null;

  const field = error.details.field;

  if (typeof field === 'string' && field !== '') return 'filter';

  return error.isNotFound ? 'not-found' : 'no-access';
}

function refusalTitle(refusal: Refusal, t: TFunction): string {
  if (refusal === 'filter') return t('shared.state.filterTitle');

  return refusal === 'no-access'
    ? t('shared.state.noAccessTitle')
    : t('shared.state.notFoundTitle');
}

function refusalBody(error: unknown, refusal: Refusal, t: TFunction): string {
  if (error instanceof ApiError && !PLAIN_REFUSALS.has(error.code))
    return resolveErrorMessage(error, t);

  if (refusal === 'filter') return t('shared.state.filterBody');

  return refusal === 'no-access' ? t('shared.state.noAccessBody') : t('shared.state.notFoundBody');
}

interface PortalHome {
  to: string;
  labelKey: string;
}

/** Each shell's own start page, which every seat of that shell may open. */
const PORTAL_HOMES: readonly (PortalHome & { prefix: string })[] = [
  { prefix: '/vendor', to: '/vendor', labelKey: 'vendor.home.nav' },
  { prefix: '/platform', to: '/platform', labelKey: 'platform.nav.overview' },
];
const TENANT_HOME: PortalHome = { to: '/dashboard', labelKey: 'nav.dashboard' };
/** Screens outside every shell (a secure link, an activation, sign-in): no shell to go back to. */
const OUTSIDE_SHELLS = ['/actions/', '/activate/', '/login'];

/** The start page of the shell this address belongs to; none outside a shell, or when already there. */
function portalHome(pathname: string): PortalHome | null {
  if (OUTSIDE_SHELLS.some((prefix) => pathname.startsWith(prefix))) return null;

  const home =
    PORTAL_HOMES.find(({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`)) ??
    TENANT_HOME;

  return pathname === home.to ? null : home;
}

interface RefusalProps {
  error: unknown;
  refusal: Refusal;
  /** Off where the page heading already states the refusal. */
  titled?: boolean;
  /** The way out; by default the shell's start page. `null`: the page offers its own. */
  routeOut?: ReactNode;
}

/**
 * The designed answer to a refusal (PAT:91 — the fact, then the route out),
 * as the operator console's "Not available for your role" draws it: an info
 * InlineAlert, never the critical box, with no Retry and no request id.
 */
function RefusalNotice(props: RefusalProps) {
  return useInRouterContext() ? <RoutedRefusal {...props} /> : <Refused {...props} home={null} />;
}

function RoutedRefusal(props: RefusalProps) {
  const { pathname } = useLocation();

  return <Refused {...props} home={portalHome(pathname)} />;
}

function Refused({
  error,
  refusal,
  titled = true,
  routeOut,
  home,
}: RefusalProps & { home: PortalHome | null }) {
  const { t } = useTranslation();
  const action =
    routeOut !== undefined ? (
      routeOut
    ) : home !== null ? (
      <ButtonLink to={home.to} variant="secondary" size="sm">
        {t('shared.state.goTo', { place: t(home.labelKey) })}
      </ButtonLink>
    ) : null;

  // The refusal replaces a loading state after the page has opened: it is said
  // politely (a status), never as an alert — it is an answer, not a failure.
  return (
    <div role="status">
      <Alert
        tone="info"
        title={titled ? refusalTitle(refusal, t) : undefined}
        action={action ?? undefined}
      >
        {refusalBody(error, refusal, t)}
      </Alert>
    </div>
  );
}

/**
 * Renders an API failure.
 *
 * The message is resolved from the stable error CODE, falling back to the
 * server's own wording. Nothing here parses the human message string.
 *
 * A refusal (`refusalOf`) is not a failure: it is the designed no-access or
 * not-found state, with a way out (`routeOut`, the shell's start page unless
 * the caller gives its own) and no futile Retry.
 */
export function ErrorState({
  error,
  onRetry,
  routeOut,
}: {
  error: unknown;
  onRetry?: () => void;
  /** For a refusal: the way out. By default the shell's start page; `null` for none. */
  routeOut?: ReactNode;
}) {
  const { t } = useTranslation();
  const refusal = refusalOf(error);

  if (refusal !== null)
    return <RefusalNotice error={error} refusal={refusal} routeOut={routeOut} />;

  const message = resolveErrorMessage(error, t);

  return (
    <div
      role="alert"
      className="space-y-3 rounded-lg border border-critical-line bg-critical-surface p-4"
    >
      <p className="type-ui font-medium text-critical-ink">{message}</p>
      {error instanceof ApiError && error.requestId ? (
        <p className="numeric type-meta text-critical-ink/80">{error.requestId}</p>
      ) : null}
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {t('app.retry')}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * NEW-A1 — a page that could not show its record, in one line:
 *
 *   if (query.error) return <PageState error={query.error} onRetry={…} back={back} />;
 *
 * The page keeps its heading and its way back: the state is the heading (no
 * access, not found, or could not be loaded) and the page's own BackLink sits
 * above it, where it always is. A refusal has no Retry and no request id;
 * without a `back` the shell's start page is offered instead. A failure keeps
 * its message, request id and Retry.
 */
export function PageState({
  error,
  onRetry,
  back,
}: {
  error: unknown;
  onRetry?: () => void;
  /** The page's own way back (its `BackLink`), as its PageHeader would show it. */
  back?: ReactNode;
}) {
  const { t } = useTranslation();
  const refusal = refusalOf(error);

  return (
    <div className="space-y-5">
      <PageHeader
        back={back}
        title={refusal === null ? t('shared.state.loadFailedTitle') : refusalTitle(refusal, t)}
      />
      {refusal === null ? (
        <ErrorState error={error} onRetry={onRetry} />
      ) : (
        <RefusalNotice
          error={error}
          refusal={refusal}
          titled={false}
          routeOut={back ? null : undefined}
        />
      )}
    </div>
  );
}

/**
 * A4 / Q2 (portal delta, 15 Oct 2026): a page whose content is not there yet —
 * still loading, failed, or not available to this seat — keeps its heading, and
 * with it the browser tab's name. The state takes the content's place under the
 * page's own PageHeader:
 *
 *   if (query.isPending) return <PageFrame header={header}><LoadingState /></PageFrame>;
 *
 * A record page that cannot name its record yet heads it with its list's name,
 * as the console's plan and subscription records already do.
 */
export function PageFrame({ header, children }: { header: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-5">
      {header}
      {children}
    </div>
  );
}

/**
 * Codes whose SERVER message beats any fixed client string.
 *
 * The server fills in specifics the client does not have — WHICH approval level
 * has no approver, for instance. A generic "this cannot be routed" would send
 * the reader back to support to find out what we already knew.
 */
const SERVER_MESSAGE_WINS = new Set(['APPROVAL_ROUTE_UNRESOLVABLE']);

export function resolveErrorMessage(error: unknown, t: TFunction): string {
  if (error instanceof ApiError) {
    if (SERVER_MESSAGE_WINS.has(error.code) && error.message !== '') return error.message;

    const translated = t(`errors.${error.code}`);

    // Unrecognised responses can contain technical details, never user-facing copy.
    return translated === `errors.${error.code}` ? t('errors.unknown') : translated;
  }

  return t('errors.unknown');
}
